<template>
    <div class="container">
        <h3>{{ $translate('user_ratings_header', 'Användarrecensioner') }}</h3>
        <ul v-if="reviews.length">
            <li
                v-for="(reviewData, index) in reviews"
                :key="index"
            >
                <span class="rating-header">{{ reviewData.rater_name }}</span>
                <span class="posted">{{ $timestamp(reviewData.posted_at) }}</span>
                <div class="stars">
                    <img
                        v-for="i in reviewData.rating_score"
                        :key="`rated${i}`"
                        src="/star-green.webp"
                        class="card-rating__star"
                        loading="lazy"
                    >
                    <img
                        v-for="i in 5 - reviewData.rating_score"
                        :key="`unrated${i}`"
                        src="/star-gray.webp"
                        class="card-rating__star"
                        loading="lazy"
                    >
                </div>
                <span
                    v-if="reviewData.rating_header"
                    class="subject"
                >
                    {{ reviewData.rating_header }}
                </span>
                <div 
                    class="rating-body rich-text"
                    v-html="$format.comment(reviewData.rating_body)"
                />
                
                <div v-if="reviewData.reply" class="reply">
                    <span class="reply-author-name">
                        {{ reviewData.reply.author_name }}
                    </span>
                    <p class="posted">
                        {{ reviewData.reply.created_at.split("T")[0] }}
                    </p>
                    <div 
                        class="reply-text rich-text" 
                        v-html="$format.comment(reviewData.reply.comment_body)"
                    />
                </div>
            </li>
        </ul>


        <form
            v-if="!postSuccess"
            id="form"
            class="form"
        >
            <span class="form-title font-header">{{ $translate('user_ratings_form_header', 'Skriv ett omdöme') }}</span>

            <div class="form-container">
                <label for="score">{{ $translate('user_ratings_form_score', 'Ditt betyg') }}*</label>
                <div class="stars">
                    <i :class="{active: setActiveScore(1)}" class="fas fa-star" @click="review.ratingScore = 1" />
                    <i :class="{active: setActiveScore(2)}" class="fas fa-star" @click="review.ratingScore = 2" />
                    <i :class="{active: setActiveScore(3)}" class="fas fa-star" @click="review.ratingScore = 3" />
                    <i :class="{active: setActiveScore(4)}" class="fas fa-star" @click="review.ratingScore = 4" />
                    <i :class="{active: setActiveScore(5)}" class="fas fa-star" @click="review.ratingScore = 5" />
                </div>
            </div>

            <div class="form-container">
                <label for="name">{{ $translate('user_ratings_form_name', 'Ditt namn') }}*</label>
                <input
                    v-model="review.raterName"
                    name="name"
                    type="text"
                >
            </div>

            <div class="form-container">
                <label for="email">{{ $translate('user_ratings_form_email', 'Din email') }}*</label>
                <input
                    v-model="review.raterEmail"
                    name="email"
                    type="text"
                >
                <div
                    v-if="postError"
                    class="form-container__error"
                >
                    <div class="container" />
                    <div class="text">{{ $translate('user_ratings_form_invalid_email', 'Ogiltigt format') }}</div>
                </div>
            </div>

            <div class="form-container">
                <label for="header">{{ $translate('user_ratings_form_review_header', 'Ge ditt omdöme en rubrik') }}</label>
                <input
                    v-model="review.subject"
                    name="header"
                    type="text"
                >
            </div>

            <div class="form-container">
                <label for="body">{{ $translate('user_ratings_form_review_body', 'Berätta om din erfarenhet av {title}').replace('{title}', document.data.title) }}*</label>
                <textarea
                    v-model="review.ratingBody"
                    name="body"
                    rows="5"
                    cols="80"
                />
            </div>

            <Button
                :text="$translate('user_ratings_form_submit_text', 'Skicka in')"
                size="md"
                class="form-submit"
                background="green"
                :class="{disabled: !enableSubmit || posting}"
                @click="submit()"
            />
        </form>

        <div
            v-else
            class="form-success"
        >
            <h4>{{ $translate('user_ratings_form_success_text', 'Tack för ditt omdöme.') }}</h4>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        document: {
            type: Object,
            required: true,
            default: () => {}
        },
        model: {
            type: Object,
            required: true,
            default: () => {}
        }
    },
    data() {
        return {
            review: {
                ratingScore: '',
                raterName: '',
                subject: '',
                raterEmail: '',
                ratingBody: '',
                sourceId: Number(process.env.CORE_SOURCE_ID),
                brandId: ''
            },
            postSuccess: false,
            posting: false,
            postError: false
        };
    },
    computed: {
        reviews() {
            const model = new this.$models.CreditCard(this.document, this.$store);
            return [...model._ratings.data].reverse();
        },
        enableSubmit() {
            if (
                this.review.raterName.length &&
                this.review.ratingScore &&
                this.review.raterEmail.length &&
                this.review.ratingBody.length
            ) {
                return true;
            }

            return false;
        }
    },
    mounted() {
        this.review.brandId = this.document.data.core_api.core_data.core_id;
    },
    methods: {
        setActiveScore(score) {
            return score <= this.review.ratingScore;
        },
        submit() {
            this.posting = true;
            this.postError = false;

            let payload = {
                source_id: this.review.sourceId,
                brand_id: this.review.brandId,
                rating_score: this.review.ratingScore,
                rater_name: this.review.raterName,
                rating_subject: this.review.subject,
                rater_email: this.review.raterEmail,
                rating_body: this.review.ratingBody
            };

            this.$axios({
                method: 'post',
                baseURL: 'https://api.core.compary.com',
                url: '/api/v0/ratings',
                data: payload
            }).then(() => {
                this.$event.ratingSubmitted();
                this.postSuccess = true;
                this.posting = false;
            }).catch((error) => {
                if (error.response.data.error.raterEmail) {
                    this.postError = true;
                }
                this.posting = false;
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.form-container {
    gap: 0px;
    @include device(pad) {
        gap: 30px;
    }
}

.form-submit {
    width: 100%;
    @include device(pad) {
        width: unset;
    }
}

ul {
    @include grid(1,20px);

    li {
        @include spacing(padding,6);
        @include border-radius(10px);
        box-shadow: $bxs;
        @include border;
        @include grid(1, 0);
        @include font-size(14px, 8px);

        .rating-header {
            font-family: $fontHeader;
        }

        .posted {
            font-style: italic;
            opacity: .7;
            @include spacing(margin, 3, bottom);
            @include spacing(margin, 1, top);
        }

        .rating-body::v-deep {
            margin-bottom: 0 !important;
            > * {
                @include font-size(14px, 8px);
            }
        }
        
        .subject { font-weight: bold; }

        .stars {
            img { @include size(20px); }
        }
        .reply {
            background: $lightgray;
            @include spacing(margin, 3, top);
            @include spacing(padding, 3);
            @include font-size(14px, 8px);
            .reply-author-name {
                font-family: $fontHeader;
            }
            .reply-text::v-deep {
                margin-bottom: 0 !important;
                > * {
                    @include font-size(14px, 8px);
                }
            }
        }
    }
}

.form {
    margin-top: 40px;
    @include spacing(padding,6);
    @include border-radius(10px);
    box-shadow: $bxs;
    @include grid(1,30px);
    @include border;

    &-title {
        text-align: center;
        color: $green;
        @include font-size(32px,8px);
    }

    &-container {
        @include device(pad) {
            @include flex(start,start);
        }

        label {
            flex: 1;
            @include font-size(14px,8px);
            opacity: .7;
            padding-right: 20px;
        }

        input,
        textarea,
        .stars {
            flex: 3;
            @include font-size(16px,8px);
            @include device(mobile) {
                margin-top: 10px;
            }
        }

        .stars {
            i {
                font-size: 24px;
                cursor: pointer;
                color: #e6e6e6;
            }
            i.active { color: $green; }
        }

        input,
        textarea {
            background: $graybg;
            outline: none;
            width: 100%;
            padding: 10px;
            @include border(bottom,4px,#e2e2e2);
            font-family: $fontText;
            resize: vertical;

            &:focus {
                border-color: $green;
            }
        }

        &__error {
            width: 100%;
            color: darkred;
            font-weight: bold;
            font-size: 14px;
            @include flex;
            .container {
                padding-right: 20px;
                flex: 1;
            }
            .text { flex: 3; }
        }
    }

    &-submit {
        margin: auto;
        max-width: 100%;
        @include device(pad) {
            width: 300px;
        }
    }
    &-submit.disabled {
        pointer-events: none;
        box-shadow: none;
        opacity: .6;
    }


    &-success {
        @include spacing(padding,6);
        @include border-radius(10px);
        margin-top: 40px;
        box-shadow: $bxs;
    }
}
</style>
